// Parallel compute seam for the AI-Era report.
//
// Deliberately separate from src/support/assessmentScoring.ts: that engine
// produces the maturity-Stage summary every assessment can show, and it stays
// untouched. This one produces the far richer FAIR risk report (threat heatmap,
// product exposure, ranked recommendations, architecture). It is no longer
// behind an opt-in flag — it computes whenever the submission has something
// scorable (at least one technology domain). The report *composition* is driven
// by the admin-built `metadata.reportLayout`; the default detail level is read
// from it too. Both engines run side by side; a caller renders either or both.

import { buildAiEraInputs } from './adapter';
import { loadFramework } from './framework/loader';
import { computeMultiRiskProfile } from './scoring';
import { prepareReportData } from './report/prepare';
import {
  DEFAULT_DETAIL_LEVEL,
  isDetailLevel,
  type DetailLevel,
} from './shared/detail-level';
import type { MultiAssessmentInput, MultiRiskProfile } from './shared/types';
import type { ReportData } from './report/types';

/** The detail level a report opens at, read from the admin-built
 *  `metadata.reportLayout.defaultDetailLevel`; falls back to the shared default. */
function reportLayoutDetailLevel(
  assessment: { metadata?: any } | null | undefined,
): DetailLevel {
  const level = assessment?.metadata?.reportLayout?.defaultDetailLevel;
  return isDetailLevel(level) ? level : DEFAULT_DETAIL_LEVEL;
}

export type AiEraComputation = {
  input: MultiAssessmentInput;
  profile: MultiRiskProfile;
  reportData: ReportData;
};

/**
 * Compute the AI-Era report from a toolbox submission, or null when the
 * submission has nothing scorable yet (no technology domains selected). Pure
 * apart from `loadFramework`'s memoized parse; safe to call inside a useMemo.
 * `inventoryRows` come from the `data_import` question's dataset (fetched by the
 * caller via `useInventoryRows`).
 */
export function computeAiEraReport(params: {
  assessment: { metadata?: any } | null | undefined;
  answersByKey: Record<string, any>;
  inventoryRows?: any[];
  focusedRecommendationIds?: string[];
  detailLevel?: DetailLevel;
}): AiEraComputation | null {
  const { input, entities, branding } = buildAiEraInputs(
    params.answersByKey,
    params.inventoryRows ?? [],
  );
  // Nothing to score until at least one technology footprint is selected.
  if (input.useCases.length === 0) return null;

  const framework = loadFramework();
  const profile = computeMultiRiskProfile(framework, input, 'max');
  const reportData = prepareReportData({
    input,
    profile,
    framework,
    branding,
    entities,
    focusedRecommendationIds: params.focusedRecommendationIds,
    detailLevel: params.detailLevel ?? reportLayoutDetailLevel(params.assessment),
  });

  return { input, profile, reportData };
}
